import React from "react";
import OverlayRoot from "./shared/OverlayRoot";

interface MergeSharesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing?: boolean;
  shareCount: number;
}

const MergeSharesModal: React.FC<MergeSharesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing = false,
  shareCount,
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  return (
    <OverlayRoot data-section="merge-shares-modal-root"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overscroll-none"
      onClick={handleBackdropClick}
    >
      <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full mx-4 border border-white/20 shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white">Keep Your Shares?</h3>
        </div>

        {/* Content */}
        <div className="mb-8">
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            You have{" "}
            <span className="font-semibold text-white">
              {shareCount} shared {shareCount === 1 ? "album" : "albums"}
            </span>{" "}
            from when you were browsing anonymously.
          </p>
          <p className="text-gray-400 text-sm">
            Would you like to keep access to {shareCount === 1 ? "this album" : "these albums"} with your account?
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 px-6 py-3 text-white bg-gray-600/50 hover:bg-gray-600/70 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500/50"
          >
            No, Thanks
          </button>
          <button
            onClick={onConfirm}
            disabled={isProcessing}
            className="flex-1 px-6 py-3 text-white bg-blue-500 hover:bg-blue-600 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/50 flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Yes, Keep Them
              </>
            )}
          </button>
        </div>
      </div>
    </OverlayRoot>
  );
};

export default MergeSharesModal;
